// SPDX-License-Identifier: AGPL-3.0-or-later

import * as AccessibilityCommands from '@app/features/accessibility/commands/AccessibilityCommands';
import Accessibility from '@app/features/accessibility/state/Accessibility';
import {SettingsTabSection} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import type {KeybindCommand} from '@app/features/input/state/Keybind';
import Keybind from '@app/features/input/state/Keybind';
import {formatKeyCombo} from '@app/features/input/utils/KeybindUtils';
import {KeyboardKey} from '@app/features/ui/components/KeyboardKey';
import {SwitchGroup, SwitchGroupItem} from '@app/features/ui/components/SwitchGroup';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import styles from '@app/features/user/components/modals/tabs/chat_settings_tab/InputTab.module.css';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {Fragment} from 'react';

const KeybindShortcut: React.FC<{action: KeybindCommand}> = observer(({action}) => {
	const combo = Keybind.getActiveCombosForAction(action)[0] ?? null;
	if (!combo) return null;
	const parts = formatKeyCombo(combo).split(' + ');
	return (
		<>
			{parts.map((part, i) => (
				<Fragment key={i}>
					<KeyboardKey>{part}</KeyboardKey>
				</Fragment>
			))}
		</>
	);
});
export const InputTabContent: React.FC = observer(() => {
	const {t} = useLingui();
	const mobileLayout = MobileLayout;
	const {
		showGifButton,
		showMemesButton,
		showStickersButton,
		showEmojiButton,
		showMessageSendButton,
		showDefaultEmojisInExpressionAutocomplete,
		showCustomEmojisInExpressionAutocomplete,
		showStickersInExpressionAutocomplete,
		showMemesInExpressionAutocomplete,
	} = Accessibility;
	return (
		<>
			<SettingsTabSection
				title={t`Expression autocomplete (colon autocomplete)`}
				description={t`Control what appears in the expression autocomplete when you type colon. Customize what suggestions show up to match your preferences.`}
			>
				<div className={styles.sectionContent}>
					<SwitchGroup>
						<SwitchGroupItem
							label={t`Show default emojis in expression autocomplete`}
							value={showDefaultEmojisInExpressionAutocomplete}
							onChange={(value) => AccessibilityCommands.update({showDefaultEmojisInExpressionAutocomplete: value})}
						/>
						<SwitchGroupItem
							label={t`Show custom emojis in expression autocomplete`}
							value={showCustomEmojisInExpressionAutocomplete}
							onChange={(value) => AccessibilityCommands.update({showCustomEmojisInExpressionAutocomplete: value})}
						/>
						<SwitchGroupItem
							label={t`Show stickers in expression autocomplete`}
							value={showStickersInExpressionAutocomplete}
							onChange={(value) => AccessibilityCommands.update({showStickersInExpressionAutocomplete: value})}
						/>
						<SwitchGroupItem
							label={t`Show saved media in expression autocomplete`}
							value={showMemesInExpressionAutocomplete}
							onChange={(value) => AccessibilityCommands.update({showMemesInExpressionAutocomplete: value})}
						/>
					</SwitchGroup>
				</div>
			</SettingsTabSection>
			{!mobileLayout.enabled && (
				<SettingsTabSection
					title={t`Message Input Buttons`}
					description={t`Customize which buttons are visible in the message input area. Keyboard shortcuts will continue to work even if buttons are hidden.`}
				>
					<div className={styles.sectionContent}>
						<SwitchGroup>
							<SwitchGroupItem
								label={t`Show GIFs Button`}
								value={showGifButton}
								onChange={(value) => AccessibilityCommands.update({showGifButton: value})}
								shortcut={<KeybindShortcut action="chat_toggle_gif" />}
							/>
							<SwitchGroupItem
								label={t`Show Media Button`}
								value={showMemesButton}
								onChange={(value) => AccessibilityCommands.update({showMemesButton: value})}
								shortcut={<KeybindShortcut action="chat_toggle_saved_media" />}
							/>
							<SwitchGroupItem
								label={t`Show Stickers Button`}
								value={showStickersButton}
								onChange={(value) => AccessibilityCommands.update({showStickersButton: value})}
								shortcut={<KeybindShortcut action="chat_toggle_sticker" />}
							/>
							<SwitchGroupItem
								label={t`Show Emoji Button`}
								value={showEmojiButton}
								onChange={(value) => AccessibilityCommands.update({showEmojiButton: value})}
								shortcut={<KeybindShortcut action="chat_toggle_emoji" />}
							/>
							<SwitchGroupItem
								label={t`Show Send Button`}
								value={showMessageSendButton}
								onChange={(value) => AccessibilityCommands.update({showMessageSendButton: value})}
								shortcut={<KeyboardKey>↵</KeyboardKey>}
							/>
						</SwitchGroup>
					</div>
				</SettingsTabSection>
			)}
		</>
	);
});
